import React, { type FormEvent, type ReactElement, type ReactNode } from "react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
} from "@ndd/ui";
import {
  BLOCK_KIND_OPTIONS,
  LUNCH_HINT,
  PROJECT_DESCRIPTION_HINT,
  PROJECT_DESCRIPTION_LABEL,
  START_MINUTE_CHOICES,
  blockButtonLabel,
  blockTimeLabel,
  blockTimeValue,
  endMinuteChoices,
  minuteFromBlockTimeValue,
  repeatLabel,
  withStartMinute,
  type BlockTimeField,
  type BlockTimeFieldError,
  type BlockTimeForm,
  type BlockTimeRepeat,
} from "../../lib/rd-block-time.js";

// NO-877: the Block time dialog's form. Presentational, relative imports only,
// so every state renders without a provider or a portal. The dialog shell
// (block-time-dialog.tsx) owns the request.

export interface BlockTimeFormProps {
  form: BlockTimeForm;
  onChange: (form: BlockTimeForm) => void;
  /** Names the zone the times are in, such as "Eastern Time". */
  zoneLabel: string;
  /** Problems found before the save. */
  errors: readonly BlockTimeFieldError[];
  /** A refusal from the server, or a failed request, shown above the buttons. */
  failure: string | null;
  saving: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

export const BLOCK_TIME_FIELD_ID: Record<BlockTimeField, string> = {
  kind: "block-time-kind",
  description: "block-time-description",
  date: "block-time-date",
  start: "block-time-from",
  end: "block-time-to",
};

const FIELD_LABEL = "text-body font-semibold text-foreground";
const HINT = "text-body-sm text-muted-foreground";

interface PartProps {
  form: BlockTimeForm;
  onChange: (form: BlockTimeForm) => void;
  disabled: boolean;
}

function errorFor(
  errors: readonly BlockTimeFieldError[],
  field: BlockTimeField
): string | null {
  return errors.find((error) => error.field === field)?.message ?? null;
}

export function BlockTimeFormBody(props: BlockTimeFormProps): ReactElement {
  const { form, onChange, errors, saving } = props;
  const part: PartProps = { form, onChange, disabled: saving };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    props.onSubmit();
  };

  return (
    <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-5">
      <KindPicker {...part} />
      <KindDetail {...part} errors={errors} />
      <WhenRow {...part} errors={errors} zoneLabel={props.zoneLabel} />
      <RepeatField {...part} />
      {props.failure ? (
        <p role="alert" className="m-0 text-body text-destructive">
          {props.failure}
        </p>
      ) : null}
      {/* The appointment dialogs' footer: full width when stacked on a phone. */}
      <div className="flex flex-col-reverse gap-2.5 pt-1 sm:flex-row sm:items-center sm:justify-end">
        <Button
          type="button"
          variant="utility"
          disabled={saving}
          onClick={props.onClose}
        >
          Close
        </Button>
        <Button type="submit" variant="press" shape="pill" disabled={saving}>
          {saving ? "Saving…" : blockButtonLabel(form)}
        </Button>
      </div>
    </form>
  );
}

function KindPicker({ form, onChange, disabled }: PartProps): ReactElement {
  return (
    <fieldset
      id={BLOCK_TIME_FIELD_ID.kind}
      className="m-0 flex flex-col gap-2 border-0 p-0"
    >
      <legend className={cn(FIELD_LABEL, "mb-2 p-0")}>{"What it’s for"}</legend>
      <div className="grid grid-cols-3 gap-2 @max-[520px]:grid-cols-1">
        {BLOCK_KIND_OPTIONS.map((option) => {
          const selected = option.kind === form.kind;

          return (
            <label
              key={option.kind}
              className={cn(
                "flex h-[42px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-2.5 text-body",
                selected
                  ? "border-transparent bg-primary-soft shadow-(--button-selected)"
                  : "border-border"
              )}
            >
              <input
                type="radio"
                name="block-time-kind"
                value={option.kind}
                checked={selected}
                disabled={disabled}
                onChange={() => onChange({ ...form, kind: option.kind })}
                className="m-0 size-3.5 accent-primary"
              />
              {option.name}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function KindDetail({
  form,
  onChange,
  errors,
  disabled,
}: PartProps & { errors: readonly BlockTimeFieldError[] }): ReactElement | null {
  if (form.kind === "break") {
    return <p className="m-0 text-body text-muted-strong">{LUNCH_HINT}</p>;
  }

  if (form.kind === "personal") return null;

  const problem = errorFor(errors, "description");

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={BLOCK_TIME_FIELD_ID.description} className={FIELD_LABEL}>
        {PROJECT_DESCRIPTION_LABEL}
      </Label>
      <Textarea
        id={BLOCK_TIME_FIELD_ID.description}
        rows={2}
        required
        value={form.description}
        disabled={disabled}
        aria-invalid={problem !== null}
        aria-describedby="block-time-description-note"
        onChange={(event) => onChange({ ...form, description: event.target.value })}
      />
      <span
        id="block-time-description-note"
        className={problem ? "text-body-sm text-destructive" : HINT}
      >
        {problem ?? PROJECT_DESCRIPTION_HINT}
      </span>
    </div>
  );
}

function WhenRow({
  form,
  onChange,
  errors,
  disabled,
  zoneLabel,
}: PartProps & {
  errors: readonly BlockTimeFieldError[];
  zoneLabel: string;
}): ReactElement {
  const dateProblem = errorFor(errors, "date");
  const endProblem = errorFor(errors, "end");
  const problem = dateProblem ?? endProblem;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 @max-[520px]:grid-cols-2">
        <Field
          label="Date"
          htmlFor={BLOCK_TIME_FIELD_ID.date}
          className="@max-[520px]:col-span-2"
        >
          <Input
            id={BLOCK_TIME_FIELD_ID.date}
            type="date"
            value={form.date}
            disabled={disabled}
            aria-invalid={dateProblem !== null}
            onChange={(event) => onChange({ ...form, date: event.target.value })}
          />
        </Field>
        <Field label="From" htmlFor={BLOCK_TIME_FIELD_ID.start}>
          <TimeSelect
            id={BLOCK_TIME_FIELD_ID.start}
            minutes={START_MINUTE_CHOICES}
            value={form.startMinute}
            invalid={false}
            disabled={disabled}
            onPick={(minute) => onChange(withStartMinute(form, minute))}
          />
        </Field>
        <Field label="To" htmlFor={BLOCK_TIME_FIELD_ID.end}>
          <TimeSelect
            id={BLOCK_TIME_FIELD_ID.end}
            minutes={endMinuteChoices(form.startMinute)}
            value={form.endMinute}
            invalid={endProblem !== null}
            disabled={disabled}
            onPick={(minute) => onChange({ ...form, endMinute: minute })}
          />
        </Field>
      </div>
      {problem ? (
        <span className="text-body-sm text-destructive">{problem}</span>
      ) : (
        <span className={HINT}>{`Times are in ${zoneLabel}.`}</span>
      )}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  className,
  children,
}: {
  label: string;
  htmlFor: string;
  className?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <Label htmlFor={htmlFor} className={FIELD_LABEL}>
        {label}
      </Label>
      {children}
    </div>
  );
}

function TimeSelect({
  id,
  minutes,
  value,
  invalid,
  disabled,
  onPick,
}: {
  id: string;
  minutes: readonly number[];
  value: number;
  invalid: boolean;
  disabled: boolean;
  onPick: (minute: number) => void;
}): ReactElement {
  return (
    <Select
      value={blockTimeValue(value)}
      disabled={disabled}
      onValueChange={(next) => onPick(minuteFromBlockTimeValue(next))}
    >
      <SelectTrigger id={id} aria-invalid={invalid} className="font-mono text-body">
        <SelectValue>{blockTimeLabel(value)}</SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {minutes.map((minute) => (
          <SelectItem key={minute} value={blockTimeValue(minute)} className="font-mono">
            {blockTimeLabel(minute)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const REPEAT_CHOICES: readonly BlockTimeRepeat[] = ["none", "weekly"];

function RepeatField({ form, onChange, disabled }: PartProps): ReactElement {
  return (
    <Field label="Repeats" htmlFor="block-time-repeat">
      <Select
        value={form.repeat}
        disabled={disabled}
        onValueChange={(next) => onChange({ ...form, repeat: next as BlockTimeRepeat })}
      >
        <SelectTrigger id="block-time-repeat" className="text-body">
          <SelectValue>{repeatLabel(form.repeat, form.date)}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {REPEAT_CHOICES.map((choice) => (
            <SelectItem key={choice} value={choice}>
              {repeatLabel(choice, form.date)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {form.repeat === "weekly" ? (
        <span className={HINT}>Repeats every week with no end date.</span>
      ) : null}
    </Field>
  );
}
